import { css, html, htmlArray } from "../helpers/webComponents";

const styles: string = css`
    :host {
        position: absolute;
        top: 0;
        height: 100%;
        left: 0;
    }

    .minigame-container {
        width: 200px;
        display: flex;
        justify-content: flex-end;
        padding: 20px 10px 0px 10px;
        position: relative;
    }

    .minigame-container canvas {
        width: 200px;
        height: 600px;
    }

    .minigame-container .progress-bar {
        width: 10px;
        height: 500px;
        background-color: rgba(255, 255, 255, 0.7);
        display: none;
        position: absolute;
        left: 170px;
        top: 50px;
    }

    .progress-fill {
        width: 100%;
        height: 0%;
        background-color: green;
    }
`;

export class FishGameComponent extends HTMLElement {
    private fishGameCanvas: HTMLCanvasElement | null = null;

    private fishGameCtx: CanvasRenderingContext2D | null = null;

    private fishGameProgressBar: HTMLDivElement | null = null;

    private fishGameProgressFill: HTMLDivElement | null = null;

    private fishGameState: FishGameState = {
        fishY: Math.random() * 100 + 50,
        fishSpeed: (Math.random() - 0.5) * 1.6,
        barY: 60,
        barHeight: 30,
        barSpeed: 0.8,
        isFishing: true,
        catchMeter: 0,
        catchThreshold: 150,
        isUpPressed: false,
        isDownPressed: false,
    };

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.renderFishMinigame();
        this.startMinigame();
    }

    public renderFishMinigame(): void {
        if (!this.shadowRoot) {
            return;
        }

        const element: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
            ${this.renderHTMLElements()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...element);
    }

    public startMinigame(): void {
        this.fishGameCanvas = this.shadowRoot?.querySelector("canvas") as HTMLCanvasElement;
        this.fishGameCtx = this.fishGameCanvas.getContext("2d") ?? null;
        this.fishGameProgressBar = this.shadowRoot?.querySelector(".progress-bar") as HTMLDivElement;
        this.fishGameProgressFill = this.shadowRoot?.querySelector(".progress-fill") as HTMLDivElement;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!this.fishGameCanvas || !this.fishGameCtx || !this.fishGameProgressBar || !this.fishGameProgressFill) {
            return;
        }

        this.addFishGameEventListeners();
        this.fishGameProgressBar.style.display = "block";
        this.fishGameCanvas.style.display = "block";
        this.fishGameLoop();
    }

    /**
     * Add event listeners for the fish minigame
     */
    private addFishGameEventListeners(): void {
        document.addEventListener("keydown", this.handleFishGameKeyDown.bind(this));
        document.addEventListener("keyup", this.handleFishGameKeyUp.bind(this));
    }

    private handleFishGameKeyDown(event: KeyboardEvent): void {
        if (event.key === "w" || event.key === "W") {
            this.fishGameState.isUpPressed = true;
        }
        else if (event.key === "s" || event.key === "S") {
            this.fishGameState.isDownPressed = true;
        }
    }

    private handleFishGameKeyUp(event: KeyboardEvent): void {
        if (event.key === "w" || event.key === "W") {
            this.fishGameState.isUpPressed = false;
        }
        else if (event.key === "s" || event.key === "S") {
            this.fishGameState.isDownPressed = false;
        }
    }

    private fishGameLoop(): void {
        if (this.fishGameState.isFishing) {
            this.updateFishGame();
            this.drawFishGame();
            requestAnimationFrame(() => this.fishGameLoop());
        }
    }

    private updateFishGame(): void {
        if (!this.fishGameState.isFishing) return;

        if (Math.random() < 0.05) {
            this.fishGameState.fishSpeed = (Math.random() - 0.5) * 2.5;
        }

        this.fishGameState.fishY += this.fishGameState.fishSpeed;
        if (this.fishGameState.fishY < 50 || this.fishGameState.fishY > 150) {
            this.fishGameState.fishSpeed *= -1;
        }

        if (this.fishGameState.isUpPressed) {
            this.fishGameState.barY -= this.fishGameState.barSpeed;
        }
        if (this.fishGameState.isDownPressed) {
            this.fishGameState.barY += this.fishGameState.barSpeed;
        }

        if (this.fishGameState.fishY > this.fishGameState.barY && this.fishGameState.fishY < this.fishGameState.barY + this.fishGameState.barHeight) {
            this.fishGameState.catchMeter++;
            if (this.fishGameState.catchMeter >= this.fishGameState.catchThreshold) {
                this.fishGameState.isFishing = false;
                this.hideFishGame();
                this.resetFishGame();
                this.giveFishAction();
            }
        }
        else {
            this.fishGameState.catchMeter = Math.max(0, this.fishGameState.catchMeter - 2);
        }

        if (this.fishGameProgressFill) {
            this.fishGameProgressFill.style.height = `${(this.fishGameState.catchMeter / this.fishGameState.catchThreshold) * 100}%`;
        }
    }

    private giveFishAction(): void {
        this.dispatchEvent(new CustomEvent("succeeded-fishing"));
    }

    private drawFishGame(): void {
        if (this.fishGameCtx && this.fishGameCanvas) {
            this.fishGameCtx.clearRect(0, 0, this.fishGameCanvas.width, this.fishGameCanvas.height);

            // Center the water rectangle
            const waterX: number = (this.fishGameCanvas.width - 100) / 2;
            this.fishGameCtx.fillStyle = "#4fa3d1";
            this.fishGameCtx.fillRect(waterX, 0, 100, 150);

            // Center the fish
            const fishX: number = this.fishGameCanvas.width / 2;
            this.fishGameCtx.fillStyle = "gold";
            this.fishGameCtx.beginPath();
            this.fishGameCtx.ellipse(fishX, this.fishGameState.fishY, 15, 5, 0, 0, Math.PI * 2);
            this.fishGameCtx.fill();

            // Center the bar
            const barX: number = (this.fishGameCanvas.width - 40) / 2;
            this.fishGameCtx.fillStyle = "rgba(255, 255, 255, 0.7)";
            this.fishGameCtx.fillRect(barX, this.fishGameState.barY, 40, this.fishGameState.barHeight);
        }
    }

    private resetFishGame(): void {
        if (this.fishGameProgressBar) {
            this.fishGameProgressBar.style.display = "none";
        }
        this.fishGameState.isFishing = false;
        this.fishGameState.catchMeter = 0;
        if (this.fishGameProgressFill) {
            this.fishGameProgressFill.style.height = "0%";
        }
    }

    private hideFishGame(): void {
        if (this.fishGameCanvas) {
            this.fishGameCanvas.style.display = "none";
        }
        if (this.fishGameProgressBar) {
            this.fishGameProgressBar.style.display = "none";
        }
    }

    public renderHTMLElements(): HTMLElement[] {
        return [
            html`
        <div class="minigame-container">
            <canvas></canvas>
            <div class="progress-bar">
                <div class="progress-fill"></div>
            </div>
        </div>
        `,
        ];
    }
}

type FishGameState = {
    fishY: number;
    fishSpeed: number;
    barY: number;
    barHeight: number;
    barSpeed: number;
    isFishing: boolean;
    catchMeter: number;
    catchThreshold: number;
    isUpPressed: boolean;
    isDownPressed: boolean;
};
